import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createSport } from "../../services/api/sportsService";
import FloatingButton from "../ui/FloatingButton";
import "../../styles/features/sports.css";

const CATEGORIES = [
  "Team Sports",
  "Individual Sports",
  "Water Sports",
  "Combat Sports",
  "Racquet Sports",
  "Outdoor Sports",
  "Indoor Sports",
  "Winter Sports",
  "Athletics",
  "Fitness",
  "Other",
];

const EMPTY_FORM = {
  name: "",
  description: "",
  category: "",
  rules: "",
  equipment: "",
  min_players: 1,
  max_players: 10,
  image_url: "",
};

const SportsAdminCreate = ({ token }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "min_players" || name === "max_players"
          ? parseInt(value) || 1
          : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      setLoading(true);
      await createSport(token, formData);
      navigate("/sports-admin", { state: { successMessage: `Sport "${formData.name}" created successfully!` } });
    } catch (err) {
      setError(
        "Failed to create sport: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => navigate("/sports-admin");

  return (
    <div className="sport-modal-overlay" role="dialog" aria-modal="true" aria-label="Create New Sport">
      {/* backdrop click closes */}
      <div className="sport-modal-backdrop" onClick={handleClose} />

      <div className="sport-modal-panel">
        {/* Header */}
        <div className="sport-modal-header">
          <div className="sport-modal-title-row">
            <h2 className="sport-modal-title">➕ Create New Sport</h2>
            <button
              type="button"
              className="sport-modal-close"
              onClick={handleClose}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <p className="sport-modal-subtitle">
            Fill in the details to add a new sport to the platform.
          </p>
        </div>

        {/* Scrollable body */}
        <div className="sport-modal-body">
          {error && (
            <div className="gaming-message-floating error" style={{ marginBottom: "1rem" }}>
              <span className="message-icon">❌</span>
              <span className="message-text">{error}</span>
            </div>
          )}

          <form id="create-sport-form" onSubmit={handleSubmit} className="sport-modal-form">
            {/* Row 1 — Name + Category */}
            <div className="sport-modal-row">
              <div className="form-group-gaming">
                <label htmlFor="sc-name">🏷️ Sport Name *</label>
                <input
                  id="sc-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="gaming-input"
                  placeholder="e.g., Football, Basketball"
                  required
                />
              </div>
              <div className="form-group-gaming">
                <label htmlFor="sc-category">📂 Category *</label>
                <select
                  id="sc-category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="gaming-select"
                  required
                >
                  <option value="">Select Category</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2 — Min / Max players */}
            <div className="sport-modal-row">
              <div className="form-group-gaming">
                <label htmlFor="sc-min">👤 Min Players</label>
                <input
                  id="sc-min"
                  type="number"
                  name="min_players"
                  value={formData.min_players}
                  onChange={handleChange}
                  className="gaming-input"
                  min="1"
                  placeholder="Minimum players"
                />
              </div>
              <div className="form-group-gaming">
                <label htmlFor="sc-max">👥 Max Players</label>
                <input
                  id="sc-max"
                  type="number"
                  name="max_players"
                  value={formData.max_players}
                  onChange={handleChange}
                  className="gaming-input"
                  min="1"
                  placeholder="Maximum players"
                />
              </div>
            </div>

            {/* Description */}
            <div className="form-group-gaming">
              <label htmlFor="sc-desc">📖 Description</label>
              <textarea
                id="sc-desc"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="gaming-textarea"
                placeholder="Describe the sport, its objectives and gameplay..."
                rows="3"
              />
            </div>

            {/* Rules */}
            <div className="form-group-gaming">
              <label htmlFor="sc-rules">📋 Rules</label>
              <textarea
                id="sc-rules"
                name="rules"
                value={formData.rules}
                onChange={handleChange}
                className="gaming-textarea"
                placeholder="Basic rules and regulations..."
                rows="3"
              />
            </div>

            {/* Equipment */}
            <div className="form-group-gaming">
              <label htmlFor="sc-equip">🛠️ Equipment</label>
              <textarea
                id="sc-equip"
                name="equipment"
                value={formData.equipment}
                onChange={handleChange}
                className="gaming-textarea"
                placeholder="Required equipment and gear..."
                rows="2"
              />
            </div>

            {/* Image URL */}
            <div className="form-group-gaming">
              <label htmlFor="sc-img">🖼️ Image URL</label>
              <input
                id="sc-img"
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                className="gaming-input"
                placeholder="https://example.com/sport-image.jpg"
              />
            </div>
          </form>
        </div>

        {/* Footer — always visible, no scroll */}
        <div className="sport-modal-footer">
          <FloatingButton
            type="button"
            variant="outline"
            size="medium"
            onClick={handleClose}
            icon="❌"
          >
            <span className="btn-text">Cancel</span>
          </FloatingButton>
          <FloatingButton
            type="submit"
            form="create-sport-form"
            variant="success"
            size="medium"
            disabled={loading}
            icon={loading ? "⏳" : "✅"}
          >
            <span className="btn-text">{loading ? "Creating..." : "Create Sport"}</span>
          </FloatingButton>
        </div>
      </div>
    </div>
  );
};

export default SportsAdminCreate;
